<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import {
  renderPdfCanvasPreview,
  type PdfPreviewSourceRequest,
} from '@edu/shared/richtext';

const props = defineProps<{
  readonly materialId: string;
  readonly fileName: string;
  readonly previewToken?: string | null;
  readonly cacheVersion?: string | null;
}>();

const canvasHost = ref<HTMLElement | null>(null);
let canvasPreview: { dispose(): void } | null = null;
let renderVersion = 0;

const previewRequest = computed<PdfPreviewSourceRequest>(() => ({
  materialId: props.materialId,
  previewToken: props.previewToken,
  cacheVersion: props.cacheVersion,
  fileName: props.fileName,
}));

function disposeCanvasPreview() {
  canvasPreview?.dispose();
  canvasPreview = null;
}

async function renderPdf() {
  const version = ++renderVersion;
  disposeCanvasPreview();
  await nextTick();
  if (version !== renderVersion || !canvasHost.value) {
    return;
  }
  canvasPreview = renderPdfCanvasPreview(canvasHost.value, previewRequest.value);
}

watch(
  () => [props.materialId, props.fileName, props.previewToken, props.cacheVersion],
  () => {
    void renderPdf();
  },
  { immediate: true, flush: 'post' },
);

onBeforeUnmount(() => {
  renderVersion++;
  disposeCanvasPreview();
});
</script>

<template>
  <div class="pdf-preview-frame-shell">
    <div
      ref="canvasHost"
      class="pdf-preview-canvas-host"
      data-testid="pdf-preview-canvas-host"
    ></div>
  </div>
</template>

<style scoped>
.pdf-preview-frame-shell {
  position: relative;
  width: 100%;
  min-height: var(--pdf-preview-adaptive-height);
  height: var(--pdf-preview-adaptive-height);
  overflow: hidden;
}

.pdf-preview-canvas-host {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.pdf-preview-canvas-host.pdf-canvas-preview {
  height: 100%;
  min-height: 100%;
}
</style>
